import { atom, createStore } from "jotai";
import { unwrap } from 'jotai/utils'
import { libMisc } from "../lib/misc";
import { ActionDirective, ActionRecord, MessageRecord, SyncDraftStore, SyncStore } from "./sync.types";
import { libSdk, SendMultiMessageItem as SdkSendMultiMessageItem } from "../lib/sdk";
import { UpdateStreamMessage } from "../lib/sdk.types";
import { ConversationHeader, ThreadMessage } from "../types";
import { MessagingThreadLoadOlderMessagesResponse } from "../components/App/MessagingBody/MessagingThread.types";
import FlexSearch from "flexsearch";
import { _LockMechanism, LockMechanism } from "../lib/lock-mechanism";
import { WHMessagingDB } from "../lib/db";
import Dexie from "dexie";
import { FastData } from "../lib/fast-data";
import { FastDataMessages } from "../lib/fast-data.types";
import { MinWait } from "../lib/min-wait";
import { MediaQueueProcessor } from "../lib/media-queue-processor";
import { MessagingThreadFailedActionMenuAtom } from "../components/App/MessagingBody/MessagingThreadFailedActionMenu.types";
import { MessagingThreadViewImageMediaAtom } from "../components/App/MessagingBody/MessagingThread/MessagingThreadViewImageMedia.types";
import { BackgroundQueueProcessor } from "../lib/background-queue-processor";
import { BackgroundRequest, QueuedMediaMessageItem, SendMultiRequest } from "../lib/background-queue.types";

export const atomStore = createStore();
const SCROLL_FROM_BOTTOM_THRESHOLD_TO_FORCE_SCROLL_DOWN = 30;

let lastSeenSelectedConversationId = "";
let currentMessageThreadKeyCounter = 0;

export const LOADING_DIRECTIVE = "[[CBD_DIRECTIVE:loading]]";

interface AuthInfo {
    userId: number;
    username: string;
    securityKey: string;
    apiBaseUrl: string;
}

let authInfoInstance: AuthInfo;

let dbInstance: WHMessagingDB;

(() => {
    const params = new URLSearchParams(window.location.search);

    const rawUserId = params.get("userId");
    const username = params.get("username")?.trim() || "";
    const securityKey = params.get("securityKey") || "";
    let apiBaseUrl = params.get("apiBaseUrl") || "";

    if (!rawUserId) throw new Error("Missing query parameter: userId");
    const userId = parseInt(rawUserId, 10);
    if (isNaN(userId) || userId <= 0) throw new Error("Invalid query parameter: userId must be a positive number.");

    if (!username) throw new Error("Missing or empty query parameter: username");

    const securityKeyRegex = /^[a-f0-9]{64}$/;
    if (!securityKeyRegex.test(securityKey)) throw new Error("Invalid query parameter: securityKey must be a 64-character lowercase hexadecimal string.");

    if (!apiBaseUrl) throw new Error("Missing query parameter: apiBaseUrl");
    try {
        new URL(apiBaseUrl); // Validate URL format
        if (apiBaseUrl.endsWith('/')) {
            apiBaseUrl = apiBaseUrl.slice(0, -1);
        }
    } catch (e) {
        throw new Error("Invalid query parameter: apiBaseUrl is not a valid URL.");
    }

    authInfoInstance = { userId, username, securityKey, apiBaseUrl };

    const dbHash = libMisc.generateFixedLengthHex(authInfoInstance.apiBaseUrl, 16);
    const dbName = "WHMessagingDB_" + dbHash;
    dbInstance = new WHMessagingDB(dbName);
})();

export const getAuthInfo = (): AuthInfo => {
    if (!authInfoInstance) {
        // This should ideally not happen if the IIFE runs correctly.
        throw new Error("Authentication information not initialized.");
    }
    return authInfoInstance;
};

// Define the input type for the new sendMultiMessages method
interface SendMultiMessagesInputItem {
    type: "text" | "media";
    value: string | { blob: Blob; ext: string }; // ext is like "png", "jpg"
    direction: "from" | "to";
}

class SyncService {
    flexIndex = new FlexSearch.Document<{ id: string; text: string }>({
        document: {
            id: "id",
            index: ["text"]
        },
        tokenize: "forward"
    });
    lockMechanism = new _LockMechanism();
    fastData = new FastData();
    mediaQueueProcessor: MediaQueueProcessor;
    backgroundQueueProcessor: BackgroundQueueProcessor;
    openThreadScrollFromBottom = 0;
    selectConversationTappedOnce = false;
    atomAppLoaded = atom(false);
    atomLoadingMessagesProgress = atom(0);
    atomLoadingMessagesTotal = atom(0);
    atomLoadingMessagesType = atom<"text" | "media">("text");
    atomNetworkStatusOnline = atom(true);
    atomConversations = atom<ConversationHeader[]>([]);
    atomSelectedConversation = atom("");
    atomNameChangeCounter = atom(0);
    atomCurrentConversationName = atom((get) => {
        const currentSelectedConversationId = get(this.atomSelectedConversation);
        get(this.atomNameChangeCounter);
        const currentConversation = this.fastData.getConversation(currentSelectedConversationId);
        const { first_name, last_name } = currentConversation || { first_name: "(no name)", last_name: "" };
        return `${first_name} ${last_name}`.trim();
    });
    atomMessageThreadKeyCounter = atom(1);
    atomMessageThreadScrollToBottomCounter = atom(0);
    atomMessagesTopIndexMap = atom<{ [conv_id: string]: number }>({});
    atomMessagesReachedTopMap = atom<{ [conv_id: string]: boolean }>({});
    atomCanFetchMoreMessages = atom((get) => {
        const selectedConversationId = get(this.atomSelectedConversation);
        const messagesReachedTopMap = get(this.atomMessagesReachedTopMap);
        return !messagesReachedTopMap[selectedConversationId];
    });
    atomAllMessages = atom<FastDataMessages>({});
    atomMessages = atom((get) => {
        const selectedConversationId = get(this.atomSelectedConversation);
        const allMessages = get(this.atomAllMessages);
        const convMessages = allMessages[selectedConversationId] || [];
        const threadMessages: ThreadMessage[] = [];
        for (const { id, direction, text, timestamp, media, tmp_id: msgTmpId } of convMessages) {
            const failed = this.fastData.getFailedMessageStatus(id);
            const sending = this.fastData.getSendingMessageStatus(id);

            const ret: ThreadMessage = {
                id,
                tmp_id: msgTmpId || id,
                direction,
                message: media ? "" : text,
                timestamp,
                ...(failed ? { failed } : {}),
                ...(sending ? { sending } : {}),
                ...(media ? { media } : {}),
                ...(media ? { mediaBlobUrl: this.fastData.getMediaBlobUrl(media) } : {}),
            };
            threadMessages.push(ret);
        }
        return threadMessages;
    });
    atomIsAiThinking = atom((get) => {
        const messages = get(this.atomMessages);
        if (messages.length === 0) {
            return false;
        }
        const lastMessage = messages[messages.length - 1];
        return lastMessage.direction === "from" && lastMessage.message === LOADING_DIRECTIVE;
    });
    atomSearchFilter = atom("");
    atomVisibleConversationAndMessageMap = atom((get) => {
        const searchFilter = get(this.atomSearchFilter).trim();
        const conversations = get(this.atomConversations);
        const ret: {
            conversations: { [id: string]: boolean };
            messages: { [id: string]: boolean };
        } = { conversations: {}, messages: {} };
        if (searchFilter !== "") {
            const searchResult = this.flexIndex.search(searchFilter);
            for (const { result } of searchResult) {
                for (const resultId of result as string[]) {
                    if (resultId.startsWith("msg:")) {
                        const messageId = resultId.substring(4);
                        ret.messages[messageId] = true;
                        const conv_id = this.fastData.getConversationIdFromMessage(messageId);
                        if (typeof conv_id === "string") {
                            ret.conversations[parseInt(conv_id)] = true;
                        }
                    }
                    else if (resultId.startsWith("conv:")) {
                        const convId = parseInt(resultId.substring(5));
                        ret.conversations[convId] = true;
                    }
                }
            }
        }
        else {
            for (const { id } of conversations) {
                ret.conversations[id] = true;
            }
        }
        return ret;
    });
    atomSearchShortcutCounter = atom(0);
    atomMessagingThreadFailedActionMenu = atom<MessagingThreadFailedActionMenuAtom>({});
    atomMessagingThreadViewImageMedia = atom<MessagingThreadViewImageMediaAtom>({ open: false });
    atomMobileThreadOpen = atom(false);
    atomCurrentConversationCategoryCounter = atom(0);
    atomCurrentConversationCategory = atom((get) => {
        const selectedConversationId = get(this.atomSelectedConversation);
        get(this.atomCurrentConversationCategoryCounter);
        return this.getConversationCategory(selectedConversationId);
    })

    constructor(public db: WHMessagingDB) {
        this.mediaQueueProcessor = new MediaQueueProcessor();
        this.backgroundQueueProcessor = new BackgroundQueueProcessor(this.db);
        (async () => {
            await this.db.reset(); // Reset should be conditional or for development only

            await this.loadIndexFromDb();
            await this.start();
        })();
    }

    getCategories() {
        return this.fastData.getCategories();
    }

    openMobileThread() {
        if (!this.selectConversationTappedOnce) {
            return;
        }
        atomStore.set(this.atomMobileThreadOpen, true);
    }

    closeMobileThread() {
        atomStore.set(this.atomMobileThreadOpen, false);
    }

    async loadIndexFromDb() {
        this.flexIndex.clear();
        for (const { key, data } of await this.db.flexMeta.toArray()) {
            this.flexIndex.import(key, data);
        }
    }

    async saveIndexToDb() {
        await this.db.flexMeta.clear();
        const entries: { key: string, data: string }[] = [];
        this.flexIndex.export((key, data) => {
            entries.push({ key, data });
        });
        for (const { key, data } of entries) {
            await this.db.flexMeta.put({ key, data });
        }
    }

    async updateDraft(text: string, mediaBlobIds: number[]) {
        const conv_id = atomStore.get(this.atomSelectedConversation);
        const currentConversation = this.fastData.getConversation(conv_id);
        if (!currentConversation) {
            console.error(`error: count not update draft, conv_id: ${conv_id} not found`);
            return;
        }
        await this.fastData.updateDraft(conv_id, text, mediaBlobIds);
    }

    getDraft(conv_id = "") {
        if (conv_id === "") {
            conv_id = atomStore.get(this.atomSelectedConversation);
        }
        return this.fastData.getDraft(conv_id) || { text: "", mediaBlobIds: [] };
    }

    getDraftMediaBlob(id: number) {
        return this.fastData.getDraftMediaBlob(id);
    }

    async removeDraftMediaBlob(id: number) {
        await this.fastData.removeDraftMediaBlob(id);
    }

    async addDraftMediaBlob(blob: Blob, ext: string) {
        return await this.fastData.addDraftMediaBlob(blob, ext);
    }

    async loadOlderMessages(): Promise<MessagingThreadLoadOlderMessagesResponse> {
        const selectedConversationId = atomStore.get(this.atomSelectedConversation);
        const wasExpanded = await this.fastData.reloadMessages(selectedConversationId, true);
        if (wasExpanded) {
            return {
                type: "new_messages",
                onReady: () => {
                    this.updateAllMessagesAtom();
                }
            };
        }
        else {
            const messagesReachedTopMap = atomStore.get(this.atomMessagesReachedTopMap);
            messagesReachedTopMap[selectedConversationId] = true;
            atomStore.set(this.atomMessagesReachedTopMap, { ...messagesReachedTopMap });
            return {
                type: "no_messages",
            };
        }
    }

    async alertMediaUrlIsReady(url: string) {
        const updatedConvIds: { [conv_id: string]: boolean } = {};
        let newMessageAddedToOpenWindow = false;
        const currentSelectedConversationId = atomStore.get(this.atomSelectedConversation);
        for (const { id, message } of await this.db.pendingMediaMessages.where("url").equals(url).toArray()) {
            let wasReplaced = false;
            let tmpMessage: MessageRecord | undefined;
            await this.db.pendingMediaMessages.delete(id!);
            await this.db.messages.add(message);
            updatedConvIds[message.conv_id] = true;
            if (message.conv_id === currentSelectedConversationId && !wasReplaced) {
                newMessageAddedToOpenWindow = true;
            }
        }

        await this.saveIndexToDb();
        for (const conv_id of Object.keys(updatedConvIds)) {
            await this.fastData.reloadMessages(conv_id);
        }
        await this.fastData.reloadConversations();
        this.updateAllMessagesAtom();
        this.updateConversationsAtom(newMessageAddedToOpenWindow);
    }

    selectConversation(conv_id: string) {
        this.selectConversationTappedOnce = true;
        const currentConversation = atomStore.get(this.atomSelectedConversation);
        if (conv_id !== currentConversation) {
            atomStore.set(this.atomSelectedConversation, conv_id);
            atomStore.set(this.atomMessageThreadKeyCounter, (x) => x + 1);
            this.closeOverlays();
        }
        else {
            this.openMobileThread();
        }
    }

    private async start() {
        const processMessage = async (message: UpdateStreamMessage) => {
            await this.lockMechanism.executeLock(async () => {
                if (message.type === "first_load_progress") {
                    atomStore.set(this.atomLoadingMessagesProgress, message.progress);
                    atomStore.set(this.atomLoadingMessagesTotal, message.total);
                }
                else if (message.type === "network_status") {
                    atomStore.set(this.atomNetworkStatusOnline, message.status === "online");
                }
                else if (message.type === "new_latest_ids") {
                    await this.db.meta.put({ key: "latestConversationId", value: message.latestConversationId });
                    await this.db.meta.put({ key: "latestMessageId", value: message.latestMessageId });
                    await this.db.meta.put({ key: "latestActionId", value: message.latestActionId });
                }
                else if (message.type === "new_records") {
                    let shouldReloadConversations = false;
                    for (const convItem of message.conv) {
                        await this.db.conversations.put(convItem);
                        shouldReloadConversations = true;
                        const { first_name, last_name } = convItem;
                        this.flexIndex.add({
                            id: `conv:${convItem.id}`,
                            text: `${first_name} ${last_name}`,
                        });
                    }
                    let newMessageAddedToOpenWindow = false;
                    const currentSelectedConversationId = atomStore.get(this.atomSelectedConversation);
                    const updatedConvIds: { [conv_id: string]: boolean } = {};
                    const messagesToUpdate: MessageRecord[] = [];
                    let ignoreThreshold = false;
                    console.log("message.msg", message.msg);
                    const messagesToAdd: MessageRecord[] = [];
                    for (const _msgItem of message.msg) {
                        const msgItem = { ..._msgItem };
                        // START CHANGE: Check for local media before queueing for download
                        if (msgItem.tmp_id && msgItem.media) {
                            const localTmpMessage = await this.db.messages.where("tmp_id").equals(msgItem.tmp_id).first();
                            if (localTmpMessage && localTmpMessage.media) {
                                msgItem.media = localTmpMessage.media; // Use local media URL                                
                            }
                        }
                        // END CHANGE

                        if (msgItem.media && !await this.db.mediaBlobs.get(msgItem.media)) {
                            const url = msgItem.media;
                            await this.db.pendingMediaMessages.put({
                                url,
                                message: msgItem,
                            });
                            this.mediaQueueProcessor.queue(url);
                        }
                        else {
                            updatedConvIds[msgItem.conv_id] = true;
                            let tmpMessage: MessageRecord | undefined;
                            if (!msgItem.tmp_id || (await this.db.messages.where("tmp_id").equals(msgItem.tmp_id).count() === 0)) {
                                if (msgItem.conv_id === currentSelectedConversationId) {
                                    newMessageAddedToOpenWindow = true;
                                }
                                // await this.db.messages.add(msgItem);
                                messagesToAdd.push(msgItem);
                                if (msgItem.text && msgItem.text.trim() !== "") {
                                    this.flexIndex.add({
                                        id: `msg:${msgItem.id}`,
                                        text: msgItem.text,
                                    });
                                }
                            }
                            else {
                                const existingMessage = await this.db.messages.where("tmp_id").equals(msgItem.tmp_id).first();
                                if (existingMessage?.text === LOADING_DIRECTIVE) {
                                    await this.db.messages.update(existingMessage.local_counter!, {
                                        text: msgItem.text
                                    });
                                    if (msgItem.conv_id === currentSelectedConversationId) {
                                        newMessageAddedToOpenWindow = true;
                                        ignoreThreshold = true;
                                    }
                                }
                            }
                            // if (msgItem.tmp_id && (tmpMessage = await this.db.messages.where("tmp_id").equals(msgItem.tmp_id).first())) {
                            //     await this.db.messages.delete(msgItem.tmp_id);
                            //     const failed = this.fastData.getFailedMessageStatus(msgItem.tmp_id);
                            //     if (failed) {
                            //         await this.fastData.setFailedMessageStatus(msgItem.tmp_id, false);
                            //         await this.fastData.setFailedMessageStatus(msgItem.id, true);
                            //     }
                            //     const sending = this.fastData.getSendingMessageStatus(msgItem.tmp_id);
                            //     if (sending) {
                            //         await this.fastData.setSendingMessageStatus(msgItem.tmp_id, false);
                            //         await this.fastData.setSendingMessageStatus(msgItem.id, true);
                            //     }
                            //     // START CHANGE: Conditional FlexIndex remove
                            //     if (tmpMessage.text && tmpMessage.text.trim() !== "") {
                            //         this.flexIndex.remove(`msg:${msgItem.tmp_id}`);
                            //     }
                            //     // END CHANGE
                            //     console.log("from", msgItem.timestamp, "to", tmpMessage.timestamp, tmpMessage.text);
                            //     msgItem.timestamp = tmpMessage.timestamp;
                            // }
                            // else {
                            //     if (msgItem.conv_id === currentSelectedConversationId) {
                            //         newMessageAddedToOpenWindow = true;
                            //     }
                            // }
                            // // console.log(await this.db.messages.put(msgItem));
                            // messagesToUpdate.push({ ...msgItem });
                            // console.log("set", msgItem, await this.db.messages.get(msgItem.id));
                            // console.log("1---", (await serviceSync.db.messages.where("conv_id").equals("1").toArray()).sort((a, b) => a.timestamp - b.timestamp))
                            // START CHANGE: Conditional FlexIndex add
                            // if (msgItem.text && msgItem.text.trim() !== "") {
                            //     this.flexIndex.add({
                            //         id: `msg:${msgItem.id}`,
                            //         text: msgItem.text,
                            //     });
                            // }
                            // END CHANGE
                        }
                    }
                    let nowCounter = Date.now();
                    messagesToAdd.map(x => ({ ...x, timestamp: nowCounter++ }));
                    await this.db.messages.bulkAdd(messagesToAdd);
                    for (const action of message.action) {
                        await this.db.pendingActions.put(action);
                    }

                    if (message.categories && typeof message.category_list_hash === 'string') {
                        await this.db.meta.put({ key: "categories", value: message.categories });
                        await this.db.meta.put({ key: "categoryListHash", value: message.category_list_hash });
                        await this.fastData.reloadCategories();
                    }

                    const leftOverActions: ActionRecord[] = [];
                    let shouldUpdateNameChangeCounter = false;
                    for (const action of await this.db.pendingActions.toArray()) {
                        const actionDirective = libMisc.safeJsonParse<ActionDirective>(action.actionJson);
                        if (actionDirective?.type === "name_change") {
                            const { conv_id, first_name, last_name } = actionDirective;
                            const result = await this.db.conversations.update(conv_id, {
                                first_name,
                                last_name,
                            });
                            if (result > 0) {
                                shouldReloadConversations = true;
                                shouldUpdateNameChangeCounter = true;
                            }
                            else {
                                leftOverActions.push(action);
                            }
                        }
                    }
                    await this.db.pendingActions.clear();
                    for (const action of leftOverActions) {
                        await this.db.pendingActions.put(action);
                    }
                    await this.saveIndexToDb();

                    for (const conv_id of Object.keys(updatedConvIds)) {
                        await this.fastData.reloadMessages(conv_id);
                    }
                    this.updateAllMessagesAtom();
                    if (shouldReloadConversations) {
                        await this.fastData.reloadConversations();
                    }
                    if (shouldUpdateNameChangeCounter) {
                        atomStore.set(this.atomNameChangeCounter, x => x + 1);
                    }
                    this.updateConversationsAtom(newMessageAddedToOpenWindow, ignoreThreshold);
                }
            });
        };
        const minWait = new MinWait(500);
        await this.fastData.initialLoad();
        this.updateAllMessagesAtom();
        if (!await this.db.meta.get("latestConversationId")) {
            await this.db.meta.put({ key: "latestConversationId", value: "0" });
            await this.db.meta.put({ key: "latestMessageId", value: "0" });
            await this.db.meta.put({ key: "latestActionId", value: "0" });
            await this.db.meta.put({ key: "categories", value: {} });
            await this.db.meta.put({ key: "categoryListHash", value: "" });
            for await (const message of libSdk.update("0", "0", "0", "", true)) {
                await processMessage(message);
            }
            for await (const { progress, total } of this.mediaQueueProcessor.waitForQueueToClear()) {
                atomStore.set(this.atomLoadingMessagesType, "media");
                atomStore.set(this.atomLoadingMessagesProgress, progress);
                atomStore.set(this.atomLoadingMessagesTotal, total);
            }
        }
        else {
            this.updateConversationsAtom();
        }
        await minWait.wait();
        this.mediaQueueProcessor.process();
        this.backgroundQueueProcessor.process(); // Start background queue processing
        atomStore.set(this.atomSelectedConversation, atomStore.get(this.atomConversations)[0]?.id || "");
        atomStore.set(this.atomAppLoaded, true);
        const latestConversationId: string = (await this.db.meta.get("latestConversationId"))?.value || "0";
        const latestMessageId: string = (await this.db.meta.get("latestMessageId"))?.value || "0";
        const latestActionId: string = (await this.db.meta.get("latestActionId"))?.value || "0";
        const categoryListHash: string = (await this.db.meta.get("categoryListHash"))?.value || "";
        for await (const message of libSdk.update(latestConversationId, latestMessageId, latestActionId, categoryListHash)) {
            await processMessage(message);
        }
    }

    updateAllMessagesAtom() {
        atomStore.set(this.atomAllMessages, { ...this.fastData.getMessages() });
    }

    private updateConversationsAtom(newMessageAddedToOpenWindow = false, ignoreThreshold = false) {
        const currentSelectedConversation = atomStore.get(this.atomSelectedConversation);
        const conversationHeaders: ConversationHeader[] = [];
        const conversations = this.fastData.getConversations();
        const allMessages = this.fastData.getMessages();
        for (const { id, first_name, last_name } of Object.values(conversations)) {
            const convMessages = allMessages[id] || [];
            const lastMessage = (convMessages.length > 0) ? convMessages[convMessages.length - 1] : null;
            const item: ConversationHeader = {
                id,
                name: `${first_name} ${last_name}`.trim(),
                lastMessage: lastMessage?.text || "",
                timestamp: lastMessage?.timestamp || 0,
            };
            conversationHeaders.push(item);
        }
        conversationHeaders.sort((a, b) => b.timestamp - a.timestamp);
        atomStore.set(this.atomConversations, conversationHeaders);
        const currentConversation = this.fastData.getConversation(currentSelectedConversation);
        if (!currentConversation) {
            atomStore.set(this.atomSelectedConversation, conversationHeaders[0]?.id || "");
        }
        if (newMessageAddedToOpenWindow && (ignoreThreshold || this.openThreadScrollFromBottom <= SCROLL_FROM_BOTTOM_THRESHOLD_TO_FORCE_SCROLL_DOWN)) {
            setTimeout(() => {
                atomStore.set(this.atomMessageThreadScrollToBottomCounter, (x) => x + 1);
            }, 200);
        }
    }

    public trackOpenThreadScrollFromBottom(scrollFromBottom: number) {
        this.openThreadScrollFromBottom = scrollFromBottom;
    }

    public async deleteMessage(id: string) {
        // const message = await this.db.messages.get(id);
        // if (!message) {
        //     console.error(`error: cannot delete message with id ${id}, message does not exist`);
        //     return;
        // }
        // await this.db.messages.delete(id);
        // await this.fastData.setFailedMessageStatus(id, false);
        // await this.fastData.setSendingMessageStatus(id, false);
        // this.flexIndex.remove(`msg:${id}`)
        // await this.saveIndexToDb();

        // await this.fastData.reloadMessages(message.conv_id);
        // this.updateAllMessagesAtom();
        // this.updateConversationsAtom();
    }

    closeOverlays() {
        atomStore.set(this.atomMessagingThreadFailedActionMenu, {});
        atomStore.set(this.atomMessagingThreadViewImageMedia, x => ({ ...x, open: false }));
    }

    public async sendMessage(payload: string | { blob: Blob, ext: string }, existingId = "") {
        this.closeOverlays();
        const conv_id = atomStore.get(this.atomSelectedConversation);
        if (typeof payload === "string" && payload.trim() === "") {
            console.error(`error: text is empty, could not send message`);
            return;
        }
        const conversation = this.fastData.getConversation(conv_id);
        if (!conversation) {
            console.error(`error: conversation with id: ${conv_id} not found, could not send message`);
            return;
        }
        let tmpId = "";
        let delayedPersistFailed: () => Promise<void>;
        let delayedPersistSending: () => Promise<void>;
        let mustScrollToBottom = false;
        if (existingId !== "") {
            delayedPersistFailed = await this.fastData.setFailedMessageStatus(existingId, false, true);
            delayedPersistSending = await this.fastData.setSendingMessageStatus(existingId, true, true);
            tmpId = existingId;
        }
        else {
            tmpId = libMisc.generateRandomString(5);
            let media = "";
            if (typeof payload !== "string") {
                media = `${libMisc.generateRandomString()}.${payload.ext}`;
                const aspectRatio = await libMisc.getImageAspectRatio(payload.blob);
                await this.db.mediaBlobs.put({
                    blob: payload.blob,
                    type: "image",
                    url: media,
                    aspectRatio
                });
            }
            const newMessage: MessageRecord = {
                id: tmpId,
                tmp_id: tmpId,
                conv_id,
                direction: "to",
                text: (typeof payload === "string") ? payload.trim() : "",
                ...((media !== "") ? { media } : {}),
                timestamp: Date.now(),
            };
            await this.db.messages.put(newMessage);
            await this.fastData.setFailedMessageStatus(tmpId, true); // This seems incorrect, should be false initially or not set
            delayedPersistFailed = await this.fastData.setFailedMessageStatus(tmpId, false, true);
            delayedPersistSending = await this.fastData.setSendingMessageStatus(tmpId, true, true);
            if (typeof payload === "string") {
                this.flexIndex.add({
                    id: `msg:${tmpId}`,
                    text: payload.trim(),
                });
            }
            await this.saveIndexToDb();
            await this.fastData.reloadMessages(conv_id);
            mustScrollToBottom = true;
        }
        this.updateAllMessagesAtom();
        this.updateConversationsAtom();
        if (mustScrollToBottom) {
            atomStore.set(this.atomMessageThreadScrollToBottomCounter, (x) => x + 1);
        }
        this.lockMechanism.executeLock(async () => {
            const result = await libSdk.send(conv_id, (typeof payload === "string") ? payload.trim() : "", tmpId, (typeof payload !== "string") ? payload : undefined);
            if (result) {
                await delayedPersistFailed?.();
                await delayedPersistSending?.();
                console.log("persisted");
            }
            else {
                await this.fastData.setFailedMessageStatus(tmpId, true);
                await this.fastData.setSendingMessageStatus(tmpId, false);
                this.updateAllMessagesAtom();
                this.updateConversationsAtom();
            }
        }).then(() => { }); // deliberately not awaiting.
    }

    public async sendMultiMessages(messagesInput: SendMultiMessagesInputItem[]): Promise<void> {
        this.closeOverlays();

        const conv_id = atomStore.get(this.atomSelectedConversation);
        const conversation = this.fastData.getConversation(conv_id);

        if (!conversation) {
            console.error(`Error: Conversation with id ${conv_id} not found. Cannot send messages.`);
            return;
        }

        if (!messagesInput || messagesInput.length === 0) {
            console.log("No messages provided to sendMultiMessages.");
            return;
        }

        const sdkMessagesToSend: SdkSendMultiMessageItem[] = [];
        let atLeastOneMessageProcessed = false;

        let nowCounter = Date.now();
        for (const messageItem of messagesInput) {
            const tmpId = libMisc.generateRandomString(5);
            let mediaPlaceholderUrl: string | undefined = undefined;
            let sdkMessageValue: string | { blob: Blob; filename: string };

            if (messageItem.type === "media") {
                const mediaValue = messageItem.value as { blob: Blob; ext: string };
                if (!mediaValue.blob || !mediaValue.ext) {
                    console.error(`Invalid media value for message item:`, messageItem);
                    continue; // Skip this malformed message
                }
                mediaPlaceholderUrl = `${libMisc.generateRandomString()}.${mediaValue.ext}`;
                const aspectRatio = await libMisc.getImageAspectRatio(mediaValue.blob);
                await this.db.mediaBlobs.put({
                    blob: mediaValue.blob,
                    type: "image",
                    url: mediaPlaceholderUrl,
                    aspectRatio,
                });

                const filename = `${libMisc.generateFixedLengthHex(libMisc.generateRandomString(20), 16)}.${mediaValue.ext}`;
                sdkMessageValue = { blob: mediaValue.blob, filename: filename };

            } else if (messageItem.type === "text") {
                const textValue = (messageItem.value as string)?.trim();
                if (!textValue) {
                    console.error(`Empty text value for message item:`, messageItem);
                    continue; // Skip empty text message
                }
                sdkMessageValue = textValue;
            } else {
                console.error(`Unknown message type for item:`, messageItem);
                continue; // Skip unknown type
            }

            const newMessageRecord: MessageRecord = {
                id: tmpId,
                tmp_id: tmpId,
                conv_id: conv_id,
                direction: messageItem.direction,
                text: messageItem.type === "text" ? (messageItem.value as string).trim() : "",
                ...(mediaPlaceholderUrl ? { media: mediaPlaceholderUrl } : {}),
                timestamp: nowCounter++,
            };
            console.log("newMessageRecord.timestamp", newMessageRecord.timestamp);

            await this.db.messages.add(newMessageRecord);
            atLeastOneMessageProcessed = true;

            if (messageItem.type === "text") {
                this.flexIndex.add({
                    id: `msg:${tmpId}`,
                    text: (messageItem.value as string).trim(),
                });
            }

            sdkMessagesToSend.push({
                type: messageItem.type,
                value: sdkMessageValue,
                tmp_id: tmpId,
                direction: messageItem.direction,
            });
        }

        if (atLeastOneMessageProcessed) {
            await this.saveIndexToDb();
            await this.fastData.reloadMessages(conv_id);
            this.updateAllMessagesAtom();
            this.updateConversationsAtom();
            atomStore.set(this.atomMessageThreadScrollToBottomCounter, (x) => x + 1);
        }

        if (sdkMessagesToSend.length > 0) {
            await this.queueSendMulti(conv_id, sdkMessagesToSend);
        }
    }

    public async queueSendMulti(convId: string, messages: SdkSendMultiMessageItem[]): Promise<void> {
        const queuedMessages: QueuedMediaMessageItem[] = [];
        const blobIdsToCleanOnError: number[] = [];

        try {
            for (const msg of messages) {
                if (msg.type === "media") {
                    const mediaValue = msg.value as { blob: Blob; filename: string };
                    const blobId = await this.db.queuedMediaBlobs.add({
                        blob: mediaValue.blob,
                        filename: mediaValue.filename
                    });
                    blobIdsToCleanOnError.push(blobId);
                    queuedMessages.push({
                        type: "media",
                        value: { blobId: blobId, filename: mediaValue.filename },
                        tmp_id: msg.tmp_id,
                        direction: msg.direction
                    });
                } else {
                    queuedMessages.push({
                        type: "text",
                        value: msg.value as string,
                        tmp_id: msg.tmp_id,
                        direction: msg.direction
                    });
                }
            }

            const sendMultiRequest: SendMultiRequest = {
                type: "sendMulti",
                convId: convId,
                messages: queuedMessages
            };
            await this.backgroundQueueProcessor.queue(sendMultiRequest);

        } catch (error) {
            console.error("Error preparing or queuing sendMulti request:", error);
            // Clean up any blobs saved to queuedMediaBlobs if queuing failed mid-way
            if (blobIdsToCleanOnError.length > 0) {
                try {
                    await this.db.queuedMediaBlobs.bulkDelete(blobIdsToCleanOnError);
                    console.log("Cleaned up orphaned blobs from queuedMediaBlobs due to queuing error.");
                } catch (cleanupError) {
                    console.error("Error cleaning up orphaned blobs from queuedMediaBlobs:", cleanupError);
                }
            }
            // Optionally, re-throw or handle the error further (e.g., notify UI)
        }
    }

    public getConversationCategory(conv_id: string): string {
        return this.fastData.getConversationCategory(conv_id);
    }

    public getUserCategorySubscriptions(user_id: string): string[] {
        return this.fastData.getUserCategorySubscriptions(user_id);
    }

    public async queueSetConversationCategory(convId: string, categoryId: string): Promise<void> {
        await this.fastData.setConversationCategory(convId, categoryId); // Optimistic local update
        await this.backgroundQueueProcessor.queue({
            type: "setConversationCategory",
            convId,
            categoryId,
        });
    }

    public async queueToggleUserCategorySubscription(categoryId: string, state: "ON" | "OFF"): Promise<void> {
        const userId = String(getAuthInfo().userId);
        await this.fastData.toggleUserCategorySubscription(userId, categoryId, state); // Optimistic local update
        await this.backgroundQueueProcessor.queue({
            type: "toggleUserCategorySubscription",
            categoryId,
            state,
        });
    }

    getCurrentConversationId() {
        return atomStore.get(this.atomSelectedConversation);
    }
}

export const serviceSync = new SyncService(dbInstance);